import { Outlet, type RouteObject } from 'react-router'
import { AuthProvider } from './auth/AuthProvider'
import { AppEntry, RedirectIfAuthenticated, RequireAuth, RequirePermission, RequirePortal } from './auth/guards'
import { HostEntry } from './auth/HostEntry'
import { APP_ROUTE_PERMISSIONS } from './nav'
import { AppShell } from './layouts/AppShell'
import { AuthLayout } from './layouts/AuthLayout'
import { PortalShell } from './layouts/PortalShell'
import { NotFound } from './pages/NotFound'
import { Login } from './pages/auth/Login'
import { RegisterHandoff } from './pages/auth/RegisterHandoff'
import { Join } from './pages/join/Join'
import { Audit } from './pages/app/Audit'
import { Dashboard } from './pages/app/Dashboard'
import { Documents } from './pages/app/Documents'
import { EmployeeProfile } from './pages/app/EmployeeProfile'
import { Health } from './pages/app/Health'
import { Development, Reports, SettingsTiles, Workflows } from './pages/app/HrModules'
import { Structure } from './pages/app/Structure'
import { RootLayout } from './layouts/RootLayout'
import { RequestTypesSettings } from './pages/app/RequestTypesSettings'
import { Leaves } from './pages/app/Leaves'
import { Payroll, PayrollPeriod } from './pages/app/Payroll'
import { PayrollSettings } from './pages/app/PayrollSettings'
import { Declarations } from './pages/app/Declarations'
import { Integrations, RuleBuilder } from './pages/app/SoonModules'
import { Time } from './pages/app/Time'
import { Employees } from './pages/app/Employees'
import { Requests } from './pages/app/Requests'
import { PortalDocuments } from './pages/portal/PortalDocuments'
import { PortalHealth } from './pages/portal/PortalHealth'
import { PortalHome } from './pages/portal/PortalHome'
import { PortalLeaves } from './pages/portal/PortalLeaves'
import { PortalGrowth, PortalPay, PortalTax } from './pages/portal/PortalPayTaxGrowth'
import { PortalRequests } from './pages/portal/PortalRequests'
import { PortalTime } from './pages/portal/PortalTime'
import { RolesPanel } from './pages/app/RolesPanel'

/**
 * Mapa routingu wg PLAN-IMPLEMENTACJI §8.1. Ekrany Etapu 1 (landing, tożsamość,
 * rejestracja) są zaimplementowane; wszystkie moduły mają realne
 * ekrany — ostatni placeholder zniknął razem z dziennikiem audytu (Etap 7).
 */
export const routes: RouteObject[] = [
  /* Korzeń: `<html lang>` za językiem interfejsu (fala 9, grupa 76). */
  {
    element: <RootLayout />,
    children: [
  /*
   * Podręcznik użytkownika (ADR-0062) stoi CELOWO poza gałęzią z sesją: bez
   * `AuthProvider` nie ma sondy `GET /auth/session`, więc gość czyta go bez
   * logowania i bez żądań do API. Ładowany leniwie — startowy bundel nie
   * rośnie o komponent podręcznika. `*` w gałęzi z sesją przegrywa ranking
   * tras z `docs/*`, więc 404 nie przechwytuje `/docs/...`.
   */
  { path: '/docs/*', lazy: () => import('./pages/docs/DocsPage') },

  /* Wszystko poza podręcznikiem widzi sesję (dawniej dostawca stał w AppRoot). */
  {
    element: (
      <AuthProvider>
        <Outlet />
      </AuthProvider>
    ),
    children: [
  /*
   * Landing tylko na apeksie; adres firmy od razu prowadzi do logowania
   * albo, z sesją, do pracy — decyduje HostEntry.
   */
  { path: '/', element: <HostEntry /> },

  {
    element: <AuthLayout />,
    children: [
      {
        element: <RedirectIfAuthenticated />,
        children: [
          { path: '/login', element: <Login /> },
          /*
           * Rejestracja organizacji żyje w portalu 33rp (ADR-0057). Trasa
           * zostaje, bo landing prowadzi tu wszystkie „Wypróbuj za darmo" —
           * ale jest ekranem przekazania, nie kreatorem.
           */
          { path: '/register', element: <RegisterHandoff /> },
        ],
      },
    ],
  },

  /*
   * Portal onboardingu ma własny layout (ciemna szyna z timeline'em procesu),
   * więc stoi poza AuthLayout. `/join/resume` obsługuje linki wznowienia
   * z e-maili procesowych — sekret sesji jedzie w `?s=`.
   */
  { path: '/join/resume', element: <Join /> },
  { path: '/join/:code', element: <Join /> },

  {
    element: <RequireAuth />,
    children: [
      {
        path: '/app',
        element: <AppShell />,
        children: [
          { index: true, element: <AppEntry /> },
          /*
           * Każdy moduł stoi za uprawnieniem z `APP_ROUTE_PERMISSIONS` — tej samej
           * mapy, z której filtruje się nawigacja. Rola bez uprawnienia dostaje
           * ekran odmowy w miejscu modułu, nie generyczny błąd wczytywania.
           */
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/dashboard']} />,
            children: [{ path: 'dashboard', element: <Dashboard />, handle: { meta: 'dashboard' } }],
          },
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/employees']} />,
            children: [
              { path: 'employees', element: <Employees />, handle: { meta: 'employees' } },
              {
                path: 'employees/:id',
                element: <EmployeeProfile />,
                handle: { meta: 'employeeProfile' },
              },
            ],
          },
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/requests']} />,
            children: [{ path: 'requests', element: <Requests />, handle: { meta: 'requests' } }],
          },
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/documents']} />,
            children: [{ path: 'documents', element: <Documents />, handle: { meta: 'documents' } }],
          },
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/time']} />,
            children: [{ path: 'time', element: <Time />, handle: { meta: 'time' } }],
          },
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/leaves']} />,
            children: [{ path: 'leaves', element: <Leaves />, handle: { meta: 'leaves' } }],
          },
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/health']} />,
            children: [{ path: 'health', element: <Health />, handle: { meta: 'health' } }],
          },
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/payroll']} />,
            children: [
              { path: 'payroll', element: <Payroll />, handle: { meta: 'payroll' } },
              {
                path: 'payroll/:id',
                element: <PayrollPeriod />,
                handle: { meta: 'payrollPeriod' },
              },
              {
                path: 'settings/payroll',
                element: <PayrollSettings />,
                handle: { meta: 'settingsPayroll' },
              },
            ],
          },
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/declarations']} />,
            children: [
              { path: 'declarations', element: <Declarations />, handle: { meta: 'declarations' } },
            ],
          },
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/workflows']} />,
            children: [{ path: 'workflows', element: <Workflows />, handle: { meta: 'workflows' } }],
          },
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/development']} />,
            children: [
              { path: 'development', element: <Development />, handle: { meta: 'development' } },
            ],
          },
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/reports']} />,
            children: [{ path: 'reports', element: <Reports />, handle: { meta: 'reports' } }],
          },
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/integrations']} />,
            children: [
              { path: 'integrations', element: <Integrations />, handle: { meta: 'integrations' } },
            ],
          },
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/settings']} />,
            children: [
              { path: 'settings', element: <SettingsTiles />, handle: { meta: 'settings' } },
              {
                path: 'settings/rules/new',
                element: <RuleBuilder />,
                handle: { meta: 'settingsRuleNew' },
              },
              /*
               * Struktura organizacyjna - dzialy da sie zalozyc przed pierwszym
               * zatrudnieniem (przeniesione z !16, ADR-0057 faza 4).
               */
              {
                path: 'settings/structure',
                element: <Structure />,
                handle: { meta: 'settingsStructure' },
              },
              /* Typy wniosków portalu pod kaflem „Słowniki" (fala 8, grupa 37; D97). */
              {
                path: 'settings/dictionaries',
                element: <RequestTypesSettings />,
                handle: { meta: 'settingsDictionaries' },
              },
            ],
          },
          {
            element: <RequirePermission anyOf={['Role : Zarządzanie']} />,
            children: [
              {
                path: 'settings/roles',
                element: <RolesPanel />,
                handle: { meta: 'settingsRoles' },
              },
            ],
          },
          {
            element: <RequirePermission anyOf={APP_ROUTE_PERMISSIONS['/app/audit']} />,
            children: [{ path: 'audit', element: <Audit />, handle: { meta: 'audit' } }],
          },
        ],
      },
      {
        element: <RequirePortal />,
        children: [
      {
        path: '/me',
        element: <PortalShell />,
        children: [
          { index: true, element: <PortalHome />, handle: { meta: 'portal' } },
          { path: 'documents', element: <PortalDocuments />, handle: { meta: 'documents' } },
          { path: 'time', element: <PortalTime />, handle: { meta: 'time' } },
          { path: 'leaves', element: <PortalLeaves />, handle: { meta: 'leaves' } },
          { path: 'health', element: <PortalHealth />, handle: { meta: 'health' } },
          { path: 'pay', element: <PortalPay />, handle: { meta: 'portalPay' } },
          { path: 'tax', element: <PortalTax />, handle: { meta: 'portalTax' } },
          { path: 'requests', element: <PortalRequests />, handle: { meta: 'portalRequests' } },
          { path: 'growth', element: <PortalGrowth />, handle: { meta: 'portalGrowth' } },
        ],
      },
        ],
      },
    ],
  },

  { path: '*', element: <NotFound /> },
    ],
  },
    ],
  },
]
